// ===========================================
// Shared invoice display helpers
// ===========================================
// Single source of truth for invoice status colours + payment-method labels,
// previously duplicated (byte-identically) across the parent/admin invoice pages
// and the invoice-detail dialog. A brand-colour or status change now happens once.

import type { InvoiceStatus, PaymentMethod } from '../api/invoices.api';

export const INVOICE_STATUS_COLORS: Record<InvoiceStatus, { bg: string; text: string }> = {
  DRAFT: { bg: '#FCF6E6', text: '#9DA5AF' },
  SENT: { bg: '#a3d9f6', text: '#4580E4' },
  PAID: { bg: '#96DAC9', text: '#080808' },
  PARTIALLY_PAID: { bg: '#FFCE00', text: '#080808' },
  OVERDUE: { bg: '#FFAE9E', text: '#ff4040' },
  CANCELLED: { bg: '#e0e0e0', text: '#9DA5AF' },
  REFUNDED: { bg: '#e0e0e0', text: '#9DA5AF' },
};

/**
 * The status word shown to users. ONE place, mirrored by the backend's PDF helper
 * `invoiceStatusLabel`: SENT reads UNPAID, and CANCELLED reads VOID (the accounting term
 * MNM asked for; the stored value stays CANCELLED, md/plan/invoice-void-reason.md D1).
 */
export function invoiceStatusLabel(status: InvoiceStatus | string): string {
  if (status === 'SENT') return 'UNPAID';
  if (status === 'CANCELLED') return 'VOID';
  return status.replace('_', ' ');
}

export interface InvoiceBalance {
  amount: number;
  label: 'Balance Due' | 'Paid in Full' | 'No payment due (void)';
  /** Something is still owed (drives the warning colour and the Pay button). */
  owing: boolean;
}

/**
 * What is still owed, as every invoice surface should show it. A VOID invoice owes nothing,
 * whatever it was issued for (the PDF and the void email say "no payment is due"; the page
 * must not say "Balance Due $2,300" beside them). Mirrors the backend `invoicePdfBalance`.
 */
export function invoiceBalance(invoice: { status: InvoiceStatus; total: string; amountPaid: string }): InvoiceBalance {
  if (invoice.status === 'CANCELLED') {
    return { amount: 0, label: 'No payment due (void)', owing: false };
  }
  const remaining = parseFloat(invoice.total) - parseFloat(invoice.amountPaid);
  return remaining > 0
    ? { amount: remaining, label: 'Balance Due', owing: true }
    : { amount: 0, label: 'Paid in Full', owing: false };
}

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  STRIPE: 'Credit Card',
  BANK_TRANSFER: 'Bank Transfer',
  CASH: 'Cash',
  CHECK: 'Cheque',
  OTHER: 'Other',
  ACCOUNT_CREDIT: 'Account credit', // #642
};

/** #642: the payments an invoice HOLDS (a voided row is shown struck through, never counted). */
export function livePayments<P extends { voidedAt?: string | null }>(invoice: { Payment?: P[] }): P[] {
  return (invoice.Payment ?? []).filter((p) => !p.voidedAt);
}

/** #642: the part of a payment that went to the family's account credit (its live ADDED), in dollars. */
export function paymentToCredit(payment: {
  FamilyCreditEntry?: Array<{ kind: string; amount: string; voidedAt: string | null }>;
}): number {
  return (payment.FamilyCreditEntry ?? [])
    .filter((e) => e.kind === 'ADDED' && !e.voidedAt)
    .reduce((sum, e) => sum + parseFloat(e.amount), 0);
}

/** #642 Phase 2 Request 7: Laura's dropdown wording for why a payment needs checking. */
export const PAYMENT_CHECK_REASON_LABELS: Record<'AMOUNT_DIFFERS' | 'NOT_IN_BANK' | 'DUPLICATE' | 'OTHER', string> = {
  AMOUNT_DIFFERS: 'Amount differs',
  NOT_IN_BANK: 'Not in bank',
  DUPLICATE: 'Duplicate',
  OTHER: 'Other',
};

/** #642 Phase 2 Request 7: the flag is open (set and not yet resolved). */
export function paymentNeedsChecking(payment: { needsCheckingAt?: string | null; checkResolvedAt?: string | null }): boolean {
  return !!payment.needsCheckingAt && !payment.checkResolvedAt;
}

/** Long-form invoice date, e.g. "5 July 2026". */
export function formatInvoiceDate(dateString: string): string {
  return new Intl.DateTimeFormat('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(dateString));
}

/** dd/mm/yyyy h:mm am, in the school's zone. */
export function formatRecordedAt(iso: string, timeZone: string): string {
  const date = new Date(iso);
  try {
    const day = new Intl.DateTimeFormat('en-AU', {
      timeZone,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
    const time = new Intl.DateTimeFormat('en-AU', {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
    return `${day} ${time}`;
  } catch {
    return date.toLocaleString('en-AU');
  }
}

/**
 * The school-local calendar DAY a payment was received, dd/mm/yyyy. Never the
 * time: a back-dated payment is stored at a noon placeholder, so its hour is
 * not something anyone recorded.
 */
export function formatPaymentDay(iso: string, timeZone: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
  try {
    return new Intl.DateTimeFormat('en-AU', { ...opts, timeZone }).format(new Date(iso));
  } catch {
    return new Intl.DateTimeFormat('en-AU', { ...opts, timeZone: 'UTC' }).format(new Date(iso));
  }
}
